import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

/**
 * ScrollConnectedPath — vertical cyan pipeline drawn down the left side of a
 * list as the user scrolls (desktop only; children handle their own mobile
 * fallback). The line is an SVG path whose pathLength is scroll-linked via
 * useScroll — transform/paint only, no layout.
 *
 * Use with the exported PathNode inside each list item: a mono-numbered node
 * on the rail that lights up (cyan fill + glow pulse) when its item is in view.
 *
 * Reduced motion: the path renders fully drawn, nodes appear lit.
 */
export default function ScrollConnectedPath({ children, className = "" }) {
  const containerRef = useRef(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  /* The head. A bar that fills tells you how far through you are; a point of
     light at the leading edge tells you the beam is travelling, which is what
     makes each node feel arrived-at rather than switched-on. Transform only. */
  const headY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const headOpacity = useTransform(
    scrollYProgress,
    [0, 0.02, 0.98, 1],
    [0, 1, 1, 0],
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Rail — desktop only. Progress via scaleY (transform-only). */}
      <div
        className="pointer-events-none absolute left-3 top-0 h-full w-px -translate-x-1/2 bg-white/10 md:left-8"
        aria-hidden
      >
        <motion.div
          className="h-full w-full origin-top bg-cyan-accent shadow-[0_0_6px_rgba(0,240,255,0.7)]"
          style={{ scaleY: reduced ? 1 : scrollYProgress, willChange: "transform" }}
        />
        {!reduced && (
          <motion.span
            aria-hidden
            className="absolute left-1/2 top-0 h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_10px_3px_rgba(0,240,255,0.85)]"
            style={{ y: headY, opacity: headOpacity, willChange: "transform" }}
          />
        )}
      </div>

      {/* Content — reserved left column the blocks cannot enter (rail center
          sits at x=32px; content starts at 112px) */}
      <div className="pl-10 md:pl-28">{children}</div>
    </div>
  );
}

/**
 * PathNode — node circle sitting on the ScrollConnectedPath rail. Render
 * inside a `relative` list item; it positions itself onto the rail
 * (content is inset md:pl-24 = 96px; rail center x = 32px → left: -64px).
 * `active` lights it up: cyan ring/fill, glow pulse, mono index turns cyan.
 * (content inset is md:pl-28 = 112px; rail center x = 32px → left: -80px)
 *
 * `top` (px, relative to the block) anchors the node beside its block's
 * kicker line; falls back to the block's vertical center. The opaque
 * bg-ink base sits above the rail (z-10) so the line terminates at the
 * node's edge instead of running through the digits.
 */
export function PathNode({ index, active, top }) {
  const reduced = useReducedMotion();
  const lit = active || reduced;

  return (
    <div
      className="absolute -left-7 z-10 -translate-x-1/2 -translate-y-1/2 md:-left-20"
      style={{ top: top ?? "50%" }}
    >
      {/* Tick joining the node to its heading. Desktop only — on a phone the
          gap is small enough that a connector reads as clutter. */}
      <motion.span
        aria-hidden
        className="absolute left-full top-1/2 hidden h-px -translate-y-1/2 bg-cyan-accent/45 md:block"
        initial={false}
        animate={{ width: lit ? 30 : 0, opacity: lit ? 1 : 0 }}
        transition={{ duration: reduced ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* Opaque base — occludes the rail line behind the node */}
      <div className="rounded-full bg-ink">
        <motion.div
          className="flex h-6 w-6 items-center justify-center rounded-full border md:h-8 md:w-8"
          animate={
            lit
              ? {
                  scale: 1.06,
                  borderColor: "rgba(0,240,255,0.8)",
                  backgroundColor: "rgba(0,240,255,0.12)",
                  boxShadow: [
                    "0 0 0px rgba(0,240,255,0)",
                    "0 0 18px rgba(0,240,255,0.7)",
                    "0 0 8px rgba(0,240,255,0.35)",
                  ],
                }
              : {
                  scale: 1,
                  borderColor: "rgba(255,255,255,0.15)",
                  backgroundColor: "rgba(5,5,5,0)",
                  boxShadow: "0 0 0px rgba(0,240,255,0)",
                }
          }
          transition={{ duration: reduced ? 0 : 0.6, ease: "easeOut" }}
        >
          <span
            className={`font-mono text-[9px] md:text-[10px] transition-colors duration-500 ${
              lit ? "text-cyan-accent" : "text-zinc-400"
            }`}
          >
            {String(index).padStart(2, "0")}
          </span>
        </motion.div>
      </div>
    </div>
  );
}
